import { LEGACY_CUSTOMER } from '../lib/i18n/customer'
import { languageButtons } from '../lib/kioskText'
import type { LayoutBindings } from './render'
import { pageIds, textIn, type LayoutDocument } from './types'
import { serviceName } from '../lib/kioskText'
import { previewMenu } from '../lib/kioskMenu'
import type { Service } from '../lib/types'
import { t, type StaffKey, type StaffLanguage, type Vars } from '../lib/staffText'
import { SOURCE_LANGUAGE } from '../lib/i18n/languages'

/** The language previews and the editor draw in; designs keep every language. */
export const PREVIEW_LANGUAGE: StaffLanguage = SOURCE_LANGUAGE

/**
 * What the editor shows instead of live data.
 *
 * Real-looking rather than obviously fake: "A-042" and "Counter 3", not "Lorem" and
 * "XXX". Somebody designing a hall board is judging whether a number is big enough
 * to read from the door, and they cannot judge that against a placeholder that is
 * not the shape of a real number.
 *
 * The one deliberate exception is the longest plausible service name, which is in
 * the list on purpose: a design that only works with short names is a design that
 * breaks on the day the customer adds "Kredit müraciəti və sənədlərin qəbulu".
 */
export function sampleBindings(
  branchName: string,
  language: string = PREVIEW_LANGUAGE,
  languages: readonly string[] = LEGACY_CUSTOMER,
): LayoutBindings {
  const l: StaffLanguage = language === 'ru' || language === 'az' ? language : 'en'
  const say = (key: StaffKey, vars?: Vars) => t(key, l, vars)
  const desk = (n: number) => say('designs.sample.desk', { n })
  return {
    branchName: branchName || say('designs.sample.branch'),
    language,
    now: undefined,

    calls: [
      { id: '1', number: 'A-042', desk: desk(1), deskNumber: '1' },
      { id: '2', number: 'B-017', desk: desk(2), deskNumber: '2' },
      { id: '3', number: 'C-113', desk: desk(3), deskNumber: '3' },
      { id: '4', number: 'A-041', desk: desk(1), deskNumber: '1' },
      { id: '5', number: 'V-006', desk: say('designs.sample.vipDesk'), deskNumber: '5' },
      { id: '6', number: 'C-112', desk: desk(3), deskNumber: '3' },
    ],

    queues: [
      { id: 'q1', name: say('designs.sample.cash'), waiting: 4, tickets: ['A-043', 'A-044', 'A-045', 'A-046'] },
      { id: 'q2', name: say('designs.sample.cards'), waiting: 1, tickets: ['B-018'] },
      { id: 'q3', name: say('designs.sample.lending'), waiting: 2, tickets: ['C-114', 'C-115'] },
      { id: 'q4', name: 'VIP', waiting: 0, tickets: [] },
    ],

    services: [
      { id: 's1', prefix: 'A', name: say('designs.sample.cashOps') },
      { id: 's2', prefix: 'A', name: say('designs.sample.transfer') },
      { id: 's3', prefix: 'B', name: say('designs.sample.cardServices') },
      { id: 's4', prefix: 'C', name: say('designs.sample.loanIntake') },
      { id: 's5', prefix: 'C', name: say('designs.sample.newAccount') },
      { id: 's6', prefix: 'V', name: say('designs.sample.vipService') },
    ],

    languages: languageButtons(languages),

    ticketNumber: 'A-042',
    ticketService: say('designs.sample.cashService'),
    ticketAhead: 3,
    ticketWait: say('designs.sample.wait', { n: 9 }),
    ticketIssuedAt: new Date(),
    ticketValidUntil: '18:00',
    qrValue: 'https://qms.example/t/sample',

    deskName: desk(3),
    deskNumber: '3',
    deskStatus: 'open',
    deskPill: 'serving',
    clerkName: say('designs.sample.clerk'),
    serviceName: say('designs.sample.cashService'),
  }
}

/** The sample calls, repeated under new numbers until there are n: every cell of a call grid is drawn. */
export function fillCalls<C extends { id: string; number: string; desk: string }>(calls: readonly C[], n: number): C[] {
  if (calls.length === 0 || n <= calls.length) return [...calls]
  const prefixes = ['A', 'B', 'C', 'V']
  const out = [...calls]
  for (let i = calls.length; i < n; i++) {
    out.push({ ...calls[i % calls.length]!, id: String(i + 1), number: `${prefixes[i % prefixes.length]}-${String(((i * 37) % 890) + 10).padStart(3, '0')}` })
  }
  return out
}

/** The service ids a kiosk document's grid element has chosen, if it has one. */
function chosenServices(document: LayoutDocument): string[] | undefined {
  return document.elements.find((el) => el.kind === 'services')?.options?.serviceIds
}

/**
 * Bindings for drawing a document as a preview, wherever one is drawn: the
 * editor's own canvas, the gallery thumbnail of a design that already exists,
 * and the "start from" tile of a template that does not yet. All three should
 * show the same thing for the same document, which is why this is the one
 * place that decides it, rather than each caller building sampleBindings and
 * a service grid on its own.
 *
 * services is the organisation's catalogue and is optional because it is not
 * always loaded yet, or does not apply — a hall display and a printed ticket
 * have no service grid to draw, and previewMenu is never asked for one. See
 * previewMenu for what stands in for the catalogue until then.
 */
export function previewBindings(
  document: LayoutDocument,
  branchName: string,
  services: readonly Service[] | undefined,
  assetURL: (id: string) => string,
): LayoutBindings {
  const sample = sampleBindings(branchName)
  return {
    ...sample,
    services: previewMenu(services, chosenServices(document), sample.services ?? [], PREVIEW_LANGUAGE),
    assetURL,
    preview: true,
    // A thumbnail is the menu as a customer first sees it: one page, not every
    // page's buttons drawn on top of each other.
    page: pageIds(document)[0],
    // A button left to say its service's or page's name says it in a thumbnail
    // too, rather than being drawn blank.
    serviceLabel: (id: string) => {
      const found = services?.find((s) => s.id === id)
      return found ? serviceName(found.name, PREVIEW_LANGUAGE, found.code) : ''
    },
    pageLabel: (id: string) => textIn(document.pages?.find((p) => p.id === id)?.name, PREVIEW_LANGUAGE),
  }
}
